import React, {useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import UserImageCircle from "../../UserImageCircle";
import {useAuth} from "../../../providers/AuthProvider";
import MeetingRatingsModal from "./MeetingRatingsModal";
import FiveStarRating from "../../FiveStarRating";
import ProposeInterviewModal from "../ProposeMeetingModal";
import Meeting from "../../../models/Meeting";
import {useProject} from "../../../providers/ClientProjectProvider";
import Popup from "../../../../js/plugins/Popup";
import MeetingPopup from "./MeetingPopup";
import InfoTooltip from '../../InfoTooltip';
import {Modal, Dropdown} from "antd";
import {useIsMutating} from "@tanstack/react-query";

const CandidateMeetingRow = ({meeting, ...props}) => {
    const {auth} = useAuth();
    const {project} = useProject();

    const authIsCreator = auth.id === meeting.creator.id;

    const [isRatingOpen, setIsRatingOpen] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false)
    const [editingMeeting, setEditingMeeting] = useState(null)

    const isSavingMeeting = useIsMutating({mutationKey: ['requestOrUpdateInterviewMutation']});

    const popupContentRef = useRef(null);

    const ratings = meeting.ratings();
    const avgRating = meeting.averageRating();
    const myRating = meeting.ratingForMember(auth.id);

    const dropdownMenuItems = useMemo(() => {
        if (!authIsCreator) return [];

        return [
            meeting.canReschedule() && { label: 'Reschedule', key: 'Reschedule' },
            meeting.canEdit() && { label: 'Edit', key: 'Edit' },
            meeting.canCancel() && { label: 'Cancel', key: 'Cancel' },
        ].filter(Boolean);
    }, [authIsCreator, meeting]);

    const closePopup = () => {
        setIsDetailsOpen(false);
    }

    const openPopup = (event) => {
        setIsDetailsOpen(true);
    }

    // const confirmArchive = async () => {
    //     const confirm = await TB.alert('warning', 'Confirm Archive',
    //         'Are you sure you want to archive this interview? Note: this can not be undone.',
    //         [{title: 'Yes'}, {title: 'Cancel'}]
    //     );
    //     if (confirm) {
    //         return props.onArchive(meeting.id);
    //     }
    // }
    const openMeetingForm = async (model, reschedule = false) => {
        await setEditingMeeting({...model, reschedule})
        return setIsScheduleMeetingOpen(true);
    }

    const closeScheduleMeeting = async () => {

        await setIsScheduleMeetingOpen(false);

        return setEditingMeeting(false)
    }

    const openRatingsForm = (e) => {
        if (e) e.stopPropagation();

        return setIsRatingOpen(true)
    }

    const rescheduleMeeting = (e) => {
        if (e) e.stopPropagation();

        if (meeting.creator.id !== auth.id) {
            return TB.toast("Sorry, only the organizer can reschedule this interview", {theme: 'danger'})
        }

        // if (props.hasProposedInterviews) {
        //     return TB.toast("Sorry, you already have an interview request pending", {theme: 'danger'})
        // }

        if(isDetailsOpen) closePopup();

        let duplicateModel = {...meeting.createFromExisting()};

        if(!meeting.requestedRescheduleByTalent()) {
            duplicateModel.declined_slots = null;
            duplicateModel.available_slots = meeting.slotsAvailableToSelect()
        }
        duplicateModel = Meeting.make({
            ...duplicateModel,
            rescheduled_at: new Date(),
            started_at: null,
            ended_at: null,
        });

        return openMeetingForm(duplicateModel, true)
    }

    const cancelMeeting = e => {
        e.stopPropagation()

        if(isDetailsOpen) closePopup();

        props.onCancel(meeting.id, Meeting.client_cancelled_reason()?.value)
    }

    const editMeeting = (e) => {
        if (e) e.stopPropagation();

        if(isDetailsOpen) closePopup();

        let clientMembers = meeting.clientMembers();
        return openMeetingForm(
            Meeting.make({
                ...meeting,
                duration: undefined,
                members: clientMembers,
                member_ids: TB.map(clientMembers || [], 'id')
            }), false)
    }

    const ratingMessage = useMemo(() => {
        let message = 'View Ratings';
        if(!ratings.length && meeting.hasPassed()) {
            message = 'No Ratings Yet'
        }else if(meeting.hasPassed() && !myRating) {
            message = 'Submit Your Rating'
        }
        return message;
    }, [ratings.length]);

    return (
        <React.Fragment>
            <Popup
                key={`open-meeting-${meeting.id}`}
                position={['center center', 'center right', 'center bottom', 'right center', 'right bottom', 'center center']}
                keepTooltipInside={'.candidate-proposal'}
                closeOnDocumentClick
                open={isDetailsOpen}
                onOpen={openPopup}
                onClose={closePopup}
                on={['click']}
                trigger={
                    <tr className="interview-row clickable border-b last-of-type:border-none hover:bg-gray-100" key={`meeting-${meeting.id}`}>

                        <td scope="row">
                            <div className="interview-name">
                                <p className="whitespace-nowrap text-xs text-ellipsis overflow-hidden max-w-[220px]">{meeting.name}</p>
                                {meeting.started_at && (
                                    <div id={`rating-${meeting.id}`} style={{ opacity: meeting.hasPassed() ? 1 : 0.75 }}>
                                        <button
                                            className="btn btn-link btn-link-info whitespace-no-wrap flex flex-col items-start"
                                            onClick={openRatingsForm}
                                            disabled={!meeting.hasPassed()}
                                        >
                                            <FiveStarRating rating={avgRating} empty={!ratings?.length || !meeting.hasPassed()}/>

                                            {ratingMessage}
                                        </button>

                                        {!meeting.hasPassed() && (
                                            <InfoTooltip
                                                placement="top"
                                                type={'gray'}
                                                target={`rating-${meeting.id}`}
                                            >
                                                Check back here after the interview to submit your rating or add internal notes
                                            </InfoTooltip>
                                        )}
                                    </div>
                                )}
                            </div>
                        </td>
                        <td>
                            {meeting.creator && (
                                <div className="flex flex-row items-center justify-start" style={{gap: 8}}>
                                    <UserImageCircle
                                        user={meeting.creator}
                                        hideTooltip={true}
                                        className="img-grayscale"
                                        size={36}/>
                                    {!!meeting.attendees().length && (
                                        <span className="attendee-count whitespace-no-wrap" id={`max-members-${meeting.id}`}>
                                        {meeting.isUserAttendee(auth.id) ?
                                            `+You, ${meeting.attendees().length - 1} more` :
                                            `+${meeting.attendees().length} more`}
                                    </span>
                                    )}
                                </div>
                            )}
                        </td>
                        <td>
                            {(meeting.requestedRescheduleByTalent() || meeting.hasExpired()) ? (
                                <button
                                    className="btn btn-primary text-xs"
                                    onClick={rescheduleMeeting}
                                    disabled={props.disabled || !meeting.canRequestNewInterview() || !authIsCreator}>
                                    Reschedule
                                </button>
                            ) : (
                                <p className="text-xs">
                                    {meeting.acceptedTime()}
                                </p>
                            )}
                        </td>
                        <td>
                            <div className={"whitespace-no-wrap meeting-status " + meeting.statusClass()}>
                                {meeting.statusName()}
                            </div>
                        </td>
                        <td>

                            {authIsCreator && !!dropdownMenuItems.length && (
                                <Dropdown
                                    menu={{
                                        items: dropdownMenuItems,
                                        onClick: e => {
                                            e.domEvent.stopPropagation();
                                            if (e.key === 'Reschedule') {
                                                rescheduleMeeting()
                                            } else if (e.key === 'Edit') {
                                                editMeeting()
                                            } else if (e.key === 'Cancel') {
                                                cancelMeeting()
                                            }
                                        },
                                        disabled: props.disabled,
                                    }} 
                                    trigger={['click']}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <i className="fas fa-ellipsis-v p-2 hover:bg-gray-200"/>
                                </Dropdown>
                            )}

                            <MeetingRatingsModal
                                disabled={props.disabled}
                                onUpdate={props.onUpdate}
                                position={props.position}
                                isOpen={isRatingOpen}
                                onToggle={() => setIsRatingOpen(!isRatingOpen)}
                                meeting={meeting}
                            />
                        </td>

                    </tr>
                }>
                <MeetingPopup
                    meeting={meeting}
                    viewerType="client"
                    onCancel={() => props.onCancel(meeting.id, Meeting.client_cancelled_reason()?.value)}
                    onEdit={editMeeting}
                    onClose={closePopup}
                    onReschedule={rescheduleMeeting}
                    actionable={authIsCreator}
                    ref={popupContentRef}/>
            </Popup>

            <Modal
                okText={null}
                zIndex={199}
                footer={null}
                cancelText={null}
                title={null}
                className="schedule-meeting-modal"
                open={isScheduleMeetingOpen}
                closeable={!isSavingMeeting?.length}
                maskClosable={!isSavingMeeting?.length}
                style={{top: 20}}
                destroyOnClose={true}
                width={800}
                onCancel={closeScheduleMeeting}
            >

                {!!editingMeeting && (
                    <ProposeInterviewModal
                        project={project}
                        position={props.position}
                        reschedule={editingMeeting?.reschedule}
                        model={editingMeeting}
                        candidate={props.candidate}
                        onClose={closeScheduleMeeting}/>
                )}
            </Modal>

        </React.Fragment>
    )
};

CandidateMeetingRow.propTypes = {
    meeting: PropTypes.any
};

export default CandidateMeetingRow;